import { User } from '~/types/user';
import { CompanyOwnership, CompanySubtypeCode } from '~/types/company';
import axios from '~/utils/axiosInstance';

interface CreateUserParams {
  email: string;
  name: string;
  role: string;
  investor: string;
  installer: string;
}

interface RejectProjectParams {
  reason: string;
  message: string;
}

interface UpdateProjectBidDeadlineParams {
  projectId: string;
  portfolioId: string;
  date: string;
}

export const getInvestors = async function (params?: { projectId?: string; portfolioId?: string }) {
  const response = await axios.get('/admin/investors', { params });
  return response?.data;
};

export const getInstallers = async function () {
  const response = await axios.get('/admin/installers');
  return response?.data;
};

export const createUser = async function ({ email, name, role, investor, installer }: CreateUserParams) {
  await axios.post('/admin/users', { email, name, role, investor, installer });
};

export interface AdminUserDetail {
  id: string;
  name: string;
  email: string;
  roleCode: string;
}

export interface UpdateAdminUserRequest {
  name?: string;
  email?: string;
  /** Set new password; omit or empty to leave unchanged */
  password?: string;
}

export const getUserById = async function (id: string) {
  const response = await axios.get<AdminUserDetail>(`/admin/users/${id}`);
  return response.data;
};

export const updateAdminUserById = async function (id: string, body: UpdateAdminUserRequest) {
  const response = await axios.patch<AdminUserDetail>(`/admin/users/${id}`, body);
  return response.data;
};

export const deleteUserById = async function (id: string) {
  await axios.delete(`/admin/users/${id}`);
};

export const rejectProject = async function (id: string, { reason, message }: RejectProjectParams) {
  const response = await axios.post(`/admin/projects/${id}/reject`, { reason, message });
  return response.data;
};

export const getUsers = async function (params: any = {}) {
  const response = await axios.get('/admin/users', { params });
  return response.data as Promise<User[]>;
};

export interface GetAdminUsersPaginatedParams {
  page: number;
  limit: number;
  sortProperty?: string;
  sortOrder?: 'asc' | 'desc';
  /** Case-insensitive substring match on name or email. */
  search?: string;
  /** Case-insensitive substring match on company / org display name. */
  companyName?: string;
  /** Comma-separated role codes (ADMIN, INVESTOR, INSTALLER, EXTERNAL). */
  roles?: string;
  /** Account status: `true` / `active`, `false` / `inactive`, or `all`. Omit = all users. */
  active?: string;
}

export interface GetAdminUsersPaginatedResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

export const getAdminUsersPaginated = async function (params: GetAdminUsersPaginatedParams) {
  const response = await axios.get<GetAdminUsersPaginatedResponse>('/admin/users', { params });
  return response.data;
};

export const patchUserActive = async function (userId: string, active: boolean) {
  const response = await axios.patch(`/admin/users/${userId}/active`, { active });
  return response.data;
};

export const updateProjectBidDeadline = async function ({ projectId, portfolioId, date }: UpdateProjectBidDeadlineParams) {
  const response = await axios.patch('/admin/bid-deadline', { projectId, portfolioId, date });
  return response.data;
};

export const getAdminNotes = async function (projectId: string) {
  const response = await axios.get(`/admin/projects/${projectId}/notes`);
  return response.data;
};

export const updateAdminNotes = async function (projectId: string, notes: string) {
  const response = await axios.patch(`/admin/projects/${projectId}/notes`, { notes });
  return response.data;
};

export const toggleDataroomUI = async function (dataroomId: string) {
  const response = await axios.patch(`/admin/dataroom/${dataroomId}/toggle-ui`);
  return response.data;
};

export const getKPIs = async function () {
  const response = await axios.get('/admin/kpis');
  return response.data;
};

export const exportKPIs = async function (filters?: any) {
  const response = await axios.post('/admin/kpis/export', { filters });
  return response.data;
};

export const exportHubspot = async function () {
  const response = await axios.post('/admin/hubspot-export');
  return response.data;
};

export interface BuyerProfileExportPollResult {
  status: 'PENDING' | 'SUCCESS' | 'ERROR';
  downloadUrl?: string;
  fileName?: string;
  recordCount?: number;
  errorMessage?: string;
}

export const exportBuyerProfiles = async function () {
  const response = await axios.post('/admin/buyer-profiles/export');
  return response.data;
};

export const pollBuyerProfileExport = async function (apiEventId: string): Promise<BuyerProfileExportPollResult> {
  const response = await axios.get(`/admin/buyer-profiles/export/${apiEventId}/poll`);
  return response.data;
};

/** One audited data point changed by an import (mirrors the server type). */
export interface BuyerProfileImportChange {
  company: string;
  questionKey: string;
  segment: string | null;
  dataPoint: string;
  type: 'CREATE' | 'EDIT' | 'DELETE';
  from: unknown;
  to: unknown;
}

export interface BuyerProfileImportSummary {
  fileName?: string | null;
  profilesProcessed: number;
  profilesChanged: number;
  /** Blank template rows for companies without a profile — no-op'd. */
  profilesSkipped?: number;
  answersCreated: number;
  answersUpdated: number;
  answersDeleted: number;
  errors: { row: number; companyId?: string; message: string }[];
  changes?: BuyerProfileImportChange[];
}

export interface BuyerProfileImportPollResult {
  status: 'PENDING' | 'SUCCESS' | 'ERROR';
  summary?: BuyerProfileImportSummary;
  errorMessage?: string;
}

export const getBuyerProfileImportUploadUrl = async function (
  fileName: string,
  fileSizeBytes: number
): Promise<{ presignedUrl: string; s3Key: string }> {
  const response = await axios.post('/admin/buyer-profiles/import/upload', { fileName, fileSizeBytes });
  return response.data;
};

export const startBuyerProfileImport = async function (params: { s3Key: string; fileName?: string }) {
  const response = await axios.post('/admin/buyer-profiles/import', params);
  return response.data;
};

export const pollBuyerProfileImport = async function (apiEventId: string): Promise<BuyerProfileImportPollResult> {
  const response = await axios.get(`/admin/buyer-profiles/import/${apiEventId}/poll`);
  return response.data;
};

export interface BuyerProfileImportRun {
  id: string;
  status: string;
  createdAt: string;
  fileName: string | null;
  errorMessage?: string;
  summary: BuyerProfileImportSummary;
  /** Current company name per company hashid referenced in this run. */
  companyNames: Record<string, string>;
}

export const listBuyerProfileImports = async function (): Promise<BuyerProfileImportRun[]> {
  const response = await axios.get('/admin/buyer-profiles/import');
  return response.data.runs ?? [];
};

export const sendNotifications = async function () {
  const response = await axios.post('/admin/notifications/send');
  return response.data;
};

export interface GetCompaniesParams {
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  /** Case-insensitive substring match on company name. */
  companyName?: string;
  /** Comma-separated: Investor, Developer, External */
  companyTypes?: string;
  /** Comma-separated: IPP, EPC, DEV */
  subtypeCodes?: string;
  /** Comma-separated hash user ids for Conductor Lead */
  ownerIds?: string;
  /** Comma-separated: A, B, C */
  thirdPartyExperiences?: string;
  /** `true` = active only, `false` = deactivated only; omit for all */
  companyActive?: string;
}

export interface GetCompaniesResponse {
  companies: CompanyOwnership[];
  total: number;
  page: number;
  limit: number;
}

export const getCompanies = async function (params: GetCompaniesParams = {}) {
  const response = await axios.get<GetCompaniesResponse>('/admin/companies', { params });
  return response.data;
};

export interface CreateCompanyParams {
  name: string;
  companyKind: 'investor' | 'developer';
  ownerId?: string;
  thirdPartyExperience?: string;
  subtypeCodes?: CompanySubtypeCode[];
}

export const createCompany = async function (params: CreateCompanyParams) {
  const response = await axios.post<CompanyOwnership>('/admin/companies', params);
  return response.data;
};

export const getCompanyOwnership = async function (companyId: string) {
  const response = await axios.get(`/admin/companies/${companyId}/ownership`);
  return response.data;
};

export const getCompanyTeam = async function (companyId: string) {
  const response = await axios.get(`/admin/companies/${companyId}/team`);
  return response.data;
};

export const postCompanyTeamInvite = async function (companyId: string, email: string) {
  const response = await axios.post(`/admin/companies/${companyId}/team`, { email });
  return response.data;
};

export const updateCompanyOwnership = async function (id: string, data: any) {
  const response = await axios.patch(`/admin/companies/${id}/ownership`, data);
  return response.data;
};

export const updateCompanySubtypes = async function (id: string, subtypeCodes: unknown) {
  const codes: string[] = Array.isArray(subtypeCodes)
    ? subtypeCodes.map(String)
    : subtypeCodes == null || subtypeCodes === ''
      ? []
      : typeof subtypeCodes === 'string'
        ? subtypeCodes.split(',').map(s => s.trim()).filter(Boolean)
        : typeof subtypeCodes === 'object'
          ? Object.values(subtypeCodes as Record<string, unknown>).map(String)
          : [String(subtypeCodes)];
  const response = await axios.patch(`/admin/companies/${id}/subtypes`, { subtypeCodes: codes });
  return response.data;
};

export const getProjectOwnership = async function (id: string) {
  const response = await axios.get(`/admin/projects/${id}/ownership`);
  return response.data;
};

export const updateProjectOwnership = async function (projectId: string, ownerId: string) {
  const response = await axios.post(`/admin/projects/${projectId}/ownership`, { ownerId });
  return response.data;
};

export const getPortfolioOwnership = async function (id: string) {
  const response = await axios.get(`/admin/portfolios/${id}/ownership`);
  return response.data;
};

export const updatePortfolioOwnership = async function (portfolioId: string, ownerId: string) {
  const response = await axios.post(`/admin/portfolios/${portfolioId}/ownership`, { ownerId });
  return response.data;
};

export const updateProjectCustomerProposal = async function (id: string, messageId: string) {
  const response = await axios.patch(`/admin/projects/${id}/customer-proposal`, { messageId });
  return response.data;
};

export const triggerIrrCalculations = async function () {
  const response = await axios.post('/admin/irr-calculations/trigger');
  return response.data;
};

export const getBids = async function (params: any = {}) {
  const response = await axios.post('/admin/bids', params);
  return response.data;
};

export const getDashboardChart = async function (
  chartId: string,
  params?: Record<string, string>
) {
  const searchParams = params ? new URLSearchParams(params).toString() : '';
  const url = `/admin/dashboard/charts/${chartId}${searchParams ? `?${searchParams}` : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export interface RecentLoginItem {
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  lastLogin: string;
}

export const getRecentLoginsFromARatedCompanies = async function () {
  const response = await axios.get<RecentLoginItem[]>('/admin/dashboard/recent-logins');
  return response.data;
};

export const getUserActivityLogs = async function (params: any = {}) {
  const response = await axios.get('/admin/user-activity', { params });
  return response.data as {
    activities: any[];
    total: number;
  };
};

export interface IngestionLogEntry {
  id: number;
  type: 'document_ingestion' | 'create_from_document' | string;
  status: 'PENDING' | 'SUCCESS' | 'ERROR' | string;
  userId: number | null;
  projectId: number | null;
  endpoint: string | null;
  idempotencyKey: string | null;
  result: unknown;
  createdAt: string;
  updatedAt: string;
  user: { id: number; email: string | null; name: string | null } | null;
  project: { id: number; name: string | null; parentId: number | null } | null;
  fileName: string | null;
  additionalContext: string | null;
  hasDocument: boolean;
}

export const getIngestionLogDocumentUrl = async function (apiEventId: number) {
  const response = await axios.get(`/admin/ingestion-log/${apiEventId}/document`);
  return response.data as { url: string; fileName: string | null };
};

export const getIngestionLog = async function (params: any = {}) {
  const response = await axios.get('/admin/ingestion-log', { params });
  return response.data as {
    entries: IngestionLogEntry[];
  };
};
  
export const getNotificationLogs = async function (params: any = {}) {
  const response = await axios.get('/admin/notifications', { params });
  return response.data as {
    notifications: any[];
    total: number;
  };
};

export interface McpTokenResponse {
  /** The bearer token. Only ever returned once, at creation. */
  token: string;
  tokenId: string;
  label: string | null;
  expiresAt: string;
  mcpUrl: string;
}

export interface CreateMcpTokenParams {
  label?: string;
  expiresInDays?: number;
}

export const createMcpToken = async function (params: CreateMcpTokenParams = {}): Promise<McpTokenResponse> {
  const response = await axios.post('/admin/mcp-tokens', params);
  return response?.data;
};

export const revokeMcpToken = async function (tokenId: string): Promise<void> {
  await axios.delete(`/admin/mcp-tokens/${encodeURIComponent(tokenId)}`);
};

export interface McpTokenSummary {
  /** Access token id or connector refresh token id. Never the token itself. */
  id: string;
  kind: 'access' | 'refresh';
  source: 'personal' | 'oauth';
  label: string | null;
  clientName: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export const listMcpTokens = async function (): Promise<McpTokenSummary[]> {
  const response = await axios.get('/admin/mcp-tokens');
  return response?.data?.tokens ?? [];
};

export type RevokeScope = 'mine' | 'everyone';

/** Revoke the caller's own tokens (`mine`) or every admin's tokens and connectors (`everyone`). */
export const revokeAllMcpTokens = async function (
  scope: RevokeScope = 'mine',
): Promise<{ revoked: number; users?: number }> {
  const response = await axios.delete('/admin/mcp-tokens', { params: { scope } });
  return response?.data;
};

export interface OauthConsentParams {
  [name: string]: string | undefined;
}

/** Record the admin's decision on an MCP OAuth request; returns the URL to send the browser to. */
export const submitOauthConsent = async function (
  decision: 'approve' | 'deny',
  params: OauthConsentParams
): Promise<{ redirectTo: string }> {
  const response = await axios.post('/admin/oauth/consent', { decision, params });
  return response?.data;
};
